import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
  LinearProgress,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import axios from 'axios';
import api from '../../../api/axiosInterceptor';
import { API_BASE_URL } from '../../../config';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { LoadingSpinner } from '../../../Components/spinner/spinner';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';

export const AdminFeCodeMasterComponent = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [formData, setFormData] = useState({
    code: '',
    fe_type: '',
    registered_type: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [uploading, setUploading] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl No.', width: 70 },
    { id: 'code', field: 'code', headerName: 'Code', width: 150 },
    { id: 'fe_type', field: 'fe_type', headerName: 'FE Type', width: 200 },
    { id: 'registered_type', field: 'registered_type', headerName: 'Registered Type', width: 200 },
    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)} disabled={uploading}>
            <Edit />
          </IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)} disabled={uploading}>
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  useEffect(() => {
    fetchFeCodes();
  }, []);

  const fetchFeCodes = () => {
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/fe-mgmt/fe-code/`)
      .then((response) => {
        const apiData = response.data.map((item, index) => ({
          id: item.id,
          slNo: index + 1,
          code: item.code,
          fe_type: item.fe_type,
          registered_type: item.registered_type,
        }));
        setRows(apiData);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Failed to fetch FE Codes:', error);
        setSnackbarMessage('Failed to load FE Codes.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      });
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleAddItem = () => {
    setIsEditMode(false);
    setFormData({
      code: '',
      fe_type: '',
      registered_type: '',
    });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleEdit = (item) => {
    setIsEditMode(true);
    setSelectedItem(item);
    setFormData({
      code: item.code,
      fe_type: item.fe_type,
      registered_type: item.registered_type,
    });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleDeleteConfirmation = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    api
      .delete(`/fe-mgmt/fe-code/${deleteItem.id}/`)
      .then(() => {
        setSnackbarMessage('FE Code deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchFeCodes();
      })
      .catch((error) => {
        console.error('Delete failed:', error);
        setSnackbarMessage('Failed to delete FE Code.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setDeleteItem(null);
      });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.code?.trim()) errors.code = 'Code is required';
    if (!formData.fe_type?.trim()) errors.fe_type = 'FE Type is required';
    if (!formData.registered_type?.trim()) errors.registered_type = 'Registered Type is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) return;
    const payload = {
      code: formData.code,
      fe_type: formData.fe_type,
      registered_type: formData.registered_type,
    };
    try {
      setUploading(true);
      if (isEditMode) {
        await api.patch(`/fe-mgmt/fe-code/${selectedItem.id}/`, payload);
        setSnackbarMessage('FE Code updated successfully.');
      } else {
        await api.post(`/fe-mgmt/fe-code/`, payload);
        setSnackbarMessage('FE Code added successfully.');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchFeCodes();
      setOpenFormDialog(false);
    } catch (error) {
      console.error('Submit failed:', error);
      setSnackbarMessage('Failed to submit FE Code.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size={30} />;
  }

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', backgroundColor: '#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor="rgba(195,234,203,0.1)" p={2}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          FE Code Master
        </Typography>
        <Button variant="contained" color="primary" onClick={handleAddItem} disabled={uploading}>
          Add
        </Button>
      </Box>
      <TableComponent
        columns={columns}
        rows={rows}
        manageColumnStatus
        enableSort
        sx={{ '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' } }}
      />
      <Dialog open={openFormDialog} onClose={() => !uploading && setOpenFormDialog(false)}>
        <DialogTitle>{isEditMode ? 'Edit FE Code' : 'Add FE Code'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Code"
            fullWidth
            value={formData.code}
            error={!!formErrors.code}
            helperText={formErrors.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="FE Type"
            fullWidth
            value={formData.fe_type}
            error={!!formErrors.fe_type}
            helperText={formErrors.fe_type}
            onChange={(e) => setFormData({ ...formData, fe_type: e.target.value })}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="Registered Type"
            fullWidth
            value={formData.registered_type}
            error={!!formErrors.registered_type}
            helperText={formErrors.registered_type}
            onChange={(e) => setFormData({ ...formData, registered_type: e.target.value })}
            disabled={uploading}
          />
          {uploading && (
            <LinearProgress sx={{ mt: 2 }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !uploading && setOpenFormDialog(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleFormSubmit} variant="contained" color="primary" disabled={uploading}>
            {isEditMode ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this FE Code?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={uploading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <SnackBarComponent open={snackbarOpen} message={snackbarMessage} severity={snackbarSeverity} onClose={handleSnackbarClose} />
    </Box>
  );
};
